/**
 * SchedaProgetto.jsx
 *
 * GRIGLIA PIXEL-PERFECT:
 *   col 1 (Obiettivo) : 350px  – span righe 1-3
 *   col 2,3,4         : 294px  – singola
 *   col wide          : 588px  – doppia (2×294)
 *
 *   Riga 1: col2=294 | col3=294 | col4=588 (wide)   → Allineamenti | Concetti | TrasversalitaMeto
 *   Riga 2: col2=294 | col3=588 (wide) | col4=294   → Priorita | Fattori | Velocita
 *   Riga 3: col2=588 (wide) | col3=294 | col4=294   → Attributi | Trasversalita | Strategia
 *
 *   Altezze fisse: riga1=195px | riga2=275px | riga3=195px
 *
 * COLORE ACCENTO: dipende da data.obiettivo
 *   cultura → C.pink   (#E91E8C)
 *   pianeta → C.blue   (#1E78C8)
 *   persone → C.gray   (#AAAAAA)
 *
 * STRATEGIA: ogni entry può avere { colore: "#hex" } per il cerchio numerato.
 *
 * IMMAGINI: metti tutto in public/assets/ → path "/assets/..." funziona senza import.
 */
 
 import React from "react";
 
 // ─── PATH ─────────────────────────────────────────────────────────────────────
 const A  = "/assets";
 const IC = "/assets/icons";
  
 // ─── PALETTE BASE (invariante) ────────────────────────────────────────────────
 const C = {
   pink:   "#E91E8C",
   blue:   "#1E78C8",
   dark:   "#222222",
   gray:   "#AAAAAA",
   light:  "#E8E8E8",
   border: "#D8D8D8",
   sep:    "#E8E8E8",
 };
  
 // colore accento per ogni obiettivo
 const ACCENT = {
   cultura: C.pink,
   pianeta: C.blue,
   persone: C.gray,
 };
  
 const ff      = "'Helvetica Neue', Arial, sans-serif";
 const ffSerif = "Georgia, 'Times New Roman', serif";
 const arial = "Arial, Arial,sans-serif";
  
 // ─── CONFIG OBIETTIVO ─────────────────────────────────────────────────────────
 const OBIETTIVO_CFG = {
   cultura: { label: "Cultura.", color: C.pink, freccia: `${A}/freccia-cultura.png` },
   pianeta: { label: "Pianeta.", color: C.blue, freccia: `${A}/freccia-pianeta.png` },
   persone: { label: "Persone.", color: C.gray, freccia: `${A}/freccia-persone.png` },
 };
  
 const CONCETTI_LABELS = ["fiducia", "rischio", "flessibilita"];
  
 function concettiImgKey(attivi) {
   if (!attivi || attivi.length === 0) return null;
   return [...attivi].sort((a, b) => a - b).map(i => CONCETTI_LABELS[i]).join("-");
 }
  
 // ─── DIMENSIONI GRIGLIA ───────────────────────────────────────────────────────
 const COL1_W  = 350;   // colonna Obiettivo
 const COL_S   = 294;   // colonna singola
 const COL_D   = 588;   // colonna doppia (2 × COL_S)
 const ROW_H   = [195, 275, 195];  // altezze righe 1-2-3
  
 // ─── STILE LABEL SEZIONE (colore accento dinamico) ────────────────────────────
 function makeSLabel(accent) {
   return {
     fontSize: 12,
     fontWeight: 400,
     letterSpacing: "0.12em",
     textTransform: "uppercase",
     color: accent,
     marginBottom: 8,
     fontFamily: ff,
   };
 }
  
 // ─── STILE CELLA BASE ─────────────────────────────────────────────────────────
 const sCellBase = {
   padding: "12px 14px",
   boxSizing: "border-box",
   fontFamily: ff,
   overflow: "hidden",
 };
  
 // ─── COMPONENTI ATOMICI ───────────────────────────────────────────────────────
 function SLabel({ accent, children }) {
   return <div style={makeSLabel(accent)}>{children}</div>;
 }
  
 function VSep() {
   return (
     <div style={{
       width: 1, height: 22,
       background: C.border,
       margin: "0 10px",
       flexShrink: 0,
     }} />
   );
 }

 function VSep2() {
    return (
      <div style={{
        width: 1, height: 42,
        background: C.border,
        margin: "0 2px",
        flexShrink: 0,
      }} />
    );
  }
  
 function HSep({ indent = 0 }) {
   return <div style={{ height: 1, background: C.sep, marginLeft: indent }} />;
 }
  
 // ─── PANNELLI ─────────────────────────────────────────────────────────────────
  
 // ── 1. OBIETTIVO ─────────────────────────────────────────────────────────────
 function ObiettivoPanel({ data, accent }) {
   const cfg = OBIETTIVO_CFG[data.obiettivo] || OBIETTIVO_CFG.cultura;
   const boxColors = [
     data.obiettivo === "cultura" ? C.pink : C.light,
     data.obiettivo === "persone" ? C.gray : C.light,
     data.obiettivo === "pianeta" ? C.blue : C.light,
     
   ];
   return (
     <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
       <SLabel accent={accent}>Obiettivo</SLabel>
  
       <div style={{
         fontSize: 58, fontWeight: 500, color: C.dark,
         lineHeight: 1, margin: "0 0 16px",
         fontFamily: ffSerif, whiteSpace: "nowrap",
       }}>
         {cfg.label}
       </div>
  
       <div style={{ display: "flex", gap: 8 }}>
         {boxColors.map((bg, i) => (
           <div key={i} style={{ width: 90, height: 90, background: bg, borderRadius: 2, flexShrink: 0 }} />
         ))}
       </div>
  
       <div style={{ height: 80 }}>
         <img src={cfg.freccia} alt="freccia"
           style={{ height: "100%", objectFit: "contain", objectPosition: "left top" }} />
       </div>
  
       <SLabel accent={accent}>Missione</SLabel>
       {data.missione.map((m, i) => (
         <div key={i} style={{
           display: "flex", gap: 5,
           fontSize: m.attiva ? 12 : 11,
           color: m.attiva ? C.dark : C.gray,
           fontWeight: m.attiva ? 700 : 400,
           marginBottom: 3, lineHeight: 1.35,
         }}>
           <span style={{ flexShrink: 0, minWidth: 22 }}>{m.numero}.</span>
           <span>{m.testo}</span>
         </div>
       ))}
     </div>
   );
 }
  
 // ── 2. ALLINEAMENTI ──────────────────────────────────────────────────────────
 function AllineamentiPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Allineamenti</SLabel>
       <img
         src={`${A}/allineamenti/${data.allineamento}.png`}
         alt={`allineamento ${data.allineamento}`}
         style={{ width: "100%", maxWidth: COL_S - 28, display: "block" }}
       />
     </div>
   );
 }
  
 // ── 3. CONCETTI CHIAVE ────────────────────────────────────────────────────────
 function ConceptiPanel({ data, accent }) {
   //const key = concettiImgKey(data.concettiAttivi);
   console.log("WERYU",data.concettiAttivi );
   const key = data.concettiAttivi;
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Concetti Chiave</SLabel>
       <img src={`${A}/concetti_chiave/${data.concettiAttivi}.png`} alt="concetti chiave"
             style={{ width: "88%", maxWidth: COL_S - 28, display: "block" }} />
       
     </div>
   );
 }
  
 // ── 4. TRASVERSALITÀ METODOLOGICHE ───────────────────────────────────────────
 // Layout fisso come screenshot:
 //   - Label in alto a sinistra
 //   - Icone con separatori in basso a sinistra (allineate in basso)
 //   - Testi lista a destra (occupano metà destra della cella wide)
 function TrasversalitaMetoPanel({ data, accent }) {
   return (
     <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
       <SLabel accent={accent}>Trasversalità Metodologiche</SLabel>
  
       {/* corpo: metà sx (icone) | metà dx (testi) */}
       <div style={{ display: "flex", flex: 1, gap: 0 }}>
  
         {/* metà sinistra: icone in basso */}
         <div style={{
           width: COL_S - 28,
           display: "flex",
           alignItems: "center",
           paddingBottom: 8,
           flexShrink: 0,
         }}>
           <div style={{ display: "flex", alignItems: "center" }}>
             {data.trasversalitaMetodologiche.map((item, i) => (                
               <React.Fragment key={i}>
                 <img
                   src={`${A}/trasversalita_metodologiche/${item.key}_${item.attiva ? "nero" : "grigio"}.png`}
                   
                   alt={item.label}
                   style={{ width: 65, height: 66, objectFit: "contain", flexShrink: 0 }}
                 />
                 {i < data.trasversalitaMetodologiche.length - 1 && <VSep2 />}
               </React.Fragment>
             ))}
           </div>
         </div>
  
         {/* metà destra: lista testi, verticalmente centrata */}
         <div style={{
           flex: 1,
           display: "flex",
           flexDirection: "column",
           justifyContent: "center",
           paddingLeft: 16,
         }}>
           {data.trasversalitaMetodologiche.map((item, i) => (
             <div key={i} style={{
               fontSize: item.attiva ? 21 : 18,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 700 : 400,
               fontStyle: "italic",
               fontFamily: arial,
               marginBottom: 4,
               //textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,
             }}>
               {item.label}
             </div>
           ))}
         </div>
       </div>
     </div>
   );
 }
  
 // ── 5. PRIORITÀ TEMATICHE ────────────────────────────────────────────────────
 function PrioritaPanel({ data, accent }) {
    return (
        <div style={{ height: "100%" }}>
          <SLabel accent={accent}>Priorità tematiche</SLabel>
          <img
            src={`${A}/priorita_tematica/${data.prioritaAttiva}.png`}
            alt={`priorita ${data.prioritaAttiva}`}
            style={{ width: "100%", maxWidth: COL_S - 28, display: "block" }}
          />
        </div>
      );  
 }
  
 // ── 6. FATTORI CHIAVE ────────────────────────────────────────────────────────
 function FattoriPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Fattori Chiave</SLabel>
       <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
         {/* icone */}
         <div style={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
           {data.fattoriChiave.map((gruppo, gi) => {
             const variant = gruppo.voci.some(v => v.attiva) ? "nero" : "grigio";
             return (
               <React.Fragment key={gi}>
                 <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 0" }}>
                   {gruppo.icone.map((ic, ii) => (
                     <img key={ii}
                       src={`${IC}/fattori/${ic.key}-${variant}.png`}
                       alt={ic.key}
                       style={{ width: 16, height: 16, objectFit: "contain" }}
                     />
                   ))}
                 </div>
                 {gi < data.fattoriChiave.length - 1 && <HSep />}
               </React.Fragment>
             );
           })}
         </div>
         {/* testi */}
         <div style={{ flex: 1, minWidth: 0 }}>
           {data.fattoriChiave.map((gruppo, gi) => (
             <React.Fragment key={gi}>
               <div style={{ padding: "6px 0" }}>
                 {gruppo.voci.map((v, vi) => (
                   <div key={vi} style={{
                     fontSize: 8.5,
                     color: v.attiva ? C.dark : C.gray,
                     fontWeight: v.attiva ? 600 : 400,
                     lineHeight: 1.5,
                   }}>
                     {v.testo}
                   </div>
                 ))}
               </div>
               {gi < data.fattoriChiave.length - 1 && <HSep />}
             </React.Fragment>
           ))}
         </div>
       </div>
     </div>
   );
 }
  
 // ── 7. VELOCITÀ ──────────────────────────────────────────────────────────────
 function VelocitaPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Velocità</SLabel>
       <img
         src={`${A}/velocita/${data.velocita}.png`}
         alt={`velocita ${data.velocita}`}
         style={{ width: "100%", maxWidth: COL_S - 28, display: "block", marginBottom: 8 }}
       />
       {data.velocitaVoci.map(v => (
         <div key={v.key} style={{
           fontSize: v.key === data.velocita ? 12 : 10,
           color:    v.key === data.velocita ? C.dark : C.gray,
           fontWeight: v.key === data.velocita ? 700 : 400,
           marginBottom: 5, lineHeight: 1.25,
         }}>
           {v.label}
         </div>
       ))}
     </div>
   );
 }
  
 // ── 8. ATTRIBUTI DI PROGETTO ─────────────────────────────────────────────────
 function AttributiPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Attributi di Progetto</SLabel>
       {data.attributiProgetto.map((item, i) => (
         <React.Fragment key={i}>
           <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}>
             <img
               src={`${IC}/attributi/${item.key}-${item.attiva ? "nero" : "grigio"}.png`}
               alt={item.label}
               style={{ width: 22, height: 22, objectFit: "contain", flexShrink: 0 }}
             />
             <span style={{
               fontSize: 10,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 600 : 400,
               lineHeight: 1.3,
               textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,
             }}>
               {item.label}
             </span>
           </div>
           {i < data.attributiProgetto.length - 1 && <HSep indent={32} />}
         </React.Fragment>
       ))}
     </div>
   );
 }
  
 // ── 9. TRASVERSALITÀ ─────────────────────────────────────────────────────────
 function TrasversalitaPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Trasversalità</SLabel>
       {data.trasversalita.map((item, i) => (
         <React.Fragment key={i}>
           <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "8px 0" }}>
             <img
               src={`${IC}/trasversalita/${item.key}-${item.attiva ? "nero" : "grigio"}.png`}
               alt={item.label}
               style={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }}
             />
             <span style={{
               fontSize: item.attiva ? 15 : 12,
               color: item.attiva ? C.dark : C.gray,
               fontWeight: item.attiva ? 700 : 400,
               fontStyle: "italic",
               fontFamily: ffSerif,
               textDecoration: item.attiva ? "underline" : "none",
               textDecorationColor: accent,
             }}>
               {item.label}
             </span>
           </div>
           {i < data.trasversalita.length - 1 && <HSep indent={40} />}
         </React.Fragment>
       ))}
     </div>
   );
 }
  
 // ── 10. STRATEGIA DI RIFERIMENTO ─────────────────────────────────────────────
 // Ogni entry può avere { colore: "#hex" } per il colore del cerchio.
 // Se non specificato usa l'accent color dell'obiettivo.
 function StrategiaPanel({ data, accent }) {
   return (
     <div style={{ height: "100%" }}>
       <SLabel accent={accent}>Principale Elemento di Strategia di Riferimento</SLabel>
       {data.strategia.map((s, i) => {
         // colore cerchio: entry.colore > accent > C.light (se non attiva)
         const circleColor = s.attiva
           ? (s.colore || accent)
           : C.light;
         return (
           <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
             <div style={{
               width: 24, height: 24, borderRadius: "50%",
               background: circleColor,
               display: "flex", alignItems: "center", justifyContent: "center",
               flexShrink: 0,
               fontSize: 10,
               color: s.attiva ? "#fff" : C.gray,
               fontWeight: 700,
             }}>
               {s.numero}
             </div>
             <span style={{
               fontSize: 12,
               color: s.attiva ? C.dark : C.gray,
               fontWeight: s.attiva ? 600 : 400,
             }}>
               {s.testo}
             </span>
           </div>
         );
       })}
     </div>
   );
 }
  
 // ─── HELPER CELLA ─────────────────────────────────────────────────────────────
 function Cell({ children, w, h, borderRight = true, borderBottom = true }) {
   return (
     <div style={{
       ...sCellBase,
       width: w,
       height: h,
       flexShrink: 0,
       borderRight:  borderRight  ? `1px solid ${C.border}` : "none",
       borderBottom: borderBottom ? `1px solid ${C.border}` : "none",
     }}>
       {children}
     </div>
   );
 }
  
 // ─── COMPONENTE ROOT ──────────────────────────────────────────────────────────
 export default function SchedaProgetto({ data }) {
   if (!data) return null;
  
   const accent = ACCENT[data.obiettivo] || C.pink;
   const totalW = COL1_W + COL_S + COL_S + COL_D; // 350+294+294+588 = 1526px
  
   return (
     <div style={{
       fontFamily: ff,
       background: "#fff",
       width: totalW,
       border: `1px solid ${C.border}`,
       boxSizing: "border-box",
       overflow: "hidden",
     }}>
  
       {/* ── CORPO GRIGLIA ── */}
       <div style={{ display: "flex", alignItems: "stretch" }}>
  
         {/* COLONNA 1 – Obiettivo, altezza totale delle 3 righe */}
         <Cell
           w={COL1_W}
           h={ROW_H[0] + ROW_H[1] + ROW_H[2]}
           borderRight
           borderBottom={false}
         >
           <ObiettivoPanel data={data} accent={accent} />
         </Cell>
  
         {/* COLONNE 2-4 × 3 RIGHE */}
         <div style={{ display: "flex", flexDirection: "column" }}>
  
           {/* RIGA 1: singola | singola | doppia */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[0]} borderRight borderBottom>
               <AllineamentiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[0]} borderRight borderBottom>
               <ConceptiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[0]} borderRight={false} borderBottom>
               <TrasversalitaMetoPanel data={data} accent={accent} />
             </Cell>
           </div>
  
           {/* RIGA 2: singola | doppia | singola */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[1]} borderRight borderBottom>
               <PrioritaPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[1]} borderRight borderBottom>
               <FattoriPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[1]} borderRight={false} borderBottom>
               <VelocitaPanel data={data} accent={accent} />
             </Cell>
           </div>
  
           {/* RIGA 3: doppia | singola | singola */}
           <div style={{ display: "flex" }}>
             <Cell w={COL_S} h={ROW_H[2]} borderRight borderBottom={false}>
               <AttributiPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_S} h={ROW_H[2]} borderRight borderBottom={false}>
               <TrasversalitaPanel data={data} accent={accent} />
             </Cell>
             <Cell w={COL_D} h={ROW_H[2]} borderRight={false} borderBottom={false}>
               <StrategiaPanel data={data} accent={accent} />
             </Cell>
           </div>
  
         </div>
       </div>
     </div>
   );
 }
  
 